/* eslint-disable indent */
/* eslint-disable react/jsx-indent */
import { useEffect, useRef } from 'react';
import { IoShieldCheckmarkOutline } from 'react-icons/io5';
import { MdOutlineFingerprint } from 'react-icons/md';
import { DeleteIcon } from '@chakra-ui/icons';
import {
  Box,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  Heading,
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  deleteSelectedInstance,
  editVariableName,
  updateResponseSelection,
} from '../../../../redux/providerBuilder/selectedInstances';
import { getSelectedInstances } from '../../../../redux/providerBuilder/selectedInstances/selectors';
import { getSelectedNetworkIndex } from '../../../../redux/providerBuilder/selectedNetworkRequest/selectors';

export const SelectedInstancesTable = () => {
  const dispatch = useAppDispatch();
  const selectedInstances = useAppSelector(getSelectedInstances);
  const selectedNetworkIndex = useAppSelector(getSelectedNetworkIndex);
  const previousLength = useRef(selectedInstances.length);
  const tableContainerRef = useRef<HTMLTableElement>(null);

  useEffect(() => {
    if (selectedInstances.length > previousLength.current) {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = tableContainerRef.current.scrollHeight;
      }
    }

    previousLength.current = selectedInstances.length;
  }, [selectedInstances.length]);

  function handleVariableNameChange(value: string, index: number) {
    dispatch(editVariableName({ value, index }));
  }

  function handleDescriptionChange(value: string, index: number) {
    const instance = selectedInstances[index];
    dispatch(
      updateResponseSelection({
        index,
        responseSelection: {
          xPath: instance.responseSelection.xPath,
          jsonPath: instance.responseSelection.jsonPath,
          responseMatch: instance.responseSelection.responseMatch,
          invariant: instance.responseSelection.invariant || false,
          matchType: instance.responseSelection.matchType,
          requestIndex: selectedNetworkIndex,
          description: value,
        },
      })
    );
  }

  return (
    <>
      <Box
        w="100%"
        // maxW='520px'
        maxW="1000px"
        margin="auto"
      >
        <Heading
          color="#000"
          textAlign="center"
          letterSpacing={0.5}
          mb={1}
          className="small-font"
        >
          Selected Data
        </Heading>
        <Box
          border="1px solid #D0D5DD"
          height="100%"
          borderRadius="12px"
          backgroundColor="transparent"
          overflow="hidden"
          minH="10vh"
          h="fit-content"
        >
          <Flex direction="column">
            <TableContainer maxH="25vh" overflowY="auto" ref={tableContainerRef}>
              <Table variant="simple">
                <Thead position="sticky" top={0} zIndex={1} bg="white">
                  <Tr>
                    <Td
                      textAlign="center"
                      border="1px solid"
                      borderColor="gray.200"
                      borderLeft="none"
                      borderTop="none"
                      w="20%"
                      py={3}
                      px={4}
                    >
                      <Heading
                        className="small-font"
                        color="#000"
                        fontWeight={600}
                        noOfLines={1}
                      >
                        Variable Name
                      </Heading>
                    </Td>
                    <Td
                      textAlign="center"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRight="none"
                      borderTop="none"
                      w="30%"
                      px={4}
                    >
                      <Heading
                        className="small-font"
                        color="#000"
                        fontWeight={600}
                        noOfLines={1}
                      >
                        Description
                      </Heading>
                    </Td>
                    <Td
                      textAlign="center"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRight="none"
                      borderTop="none"
                      w="40%"
                      py={3}
                      px={4}
                    >
                      <Heading
                        className="small-font"
                        color="#000"
                        fontWeight={600}
                        noOfLines={1}
                      >
                        Data
                      </Heading>
                    </Td>
                    <Td
                      textAlign="center"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRight="none"
                      borderTop="none"
                      w="10%"
                      py={3}
                      px={4}
                    >
                      <Heading className="small-font" color="#000" fontWeight={600}>
                        Delete
                      </Heading>
                    </Td>
                  </Tr>
                </Thead>
                <Tbody>
                  {selectedInstances
                    .filter(
                      (instance) =>
                        instance?.responseSelection?.requestIndex === selectedNetworkIndex
                    )
                    .map((instance, index) => (
                      <Tr key={`${index}_${instance.variableName}`}>
                        <Td
                          textAlign="center"
                          overflow="hidden"
                          whiteSpace="pre-wrap"
                          border="1px solid"
                          borderColor="gray.200"
                          borderLeft="none"
                          maxW="20%"
                        >
                          <Flex alignItems="center" justifyContent="center" gap={1}>
                            <Editable
                              defaultValue={instance.variableName}
                              onSubmit={(value) => handleVariableNameChange(value, index)}
                            >
                              <EditablePreview noOfLines={2} />
                              <EditableInput />
                            </Editable>

                            {instance.responseSelection.invariant && (
                              <MdOutlineFingerprint fontSize="1rem" color="black" />
                            )}
                            {instance.responseSelection.hash === 'oprf' && (
                              <IoShieldCheckmarkOutline fontSize="1rem" color="black" />
                            )}
                          </Flex>
                        </Td>
                        <Td
                          textAlign="center"
                          overflow="hidden"
                          whiteSpace="pre-wrap"
                          border="1px solid"
                          borderColor="gray.200"
                          borderLeft="none"
                          maxW="30%"
                        >
                          <Flex alignItems="center" justifyContent="center" gap={1}>
                            <Editable
                              defaultValue={
                                instance.responseSelection.description ||
                                'add description here...'
                              }
                              onSubmit={(value) => handleDescriptionChange(value, index)}
                            >
                              <EditablePreview noOfLines={2} />
                              <EditableInput />
                            </Editable>
                          </Flex>
                        </Td>
                        <Td
                          textAlign="center"
                          overflow="hidden"
                          whiteSpace="pre-wrap"
                          border="1px solid"
                          borderColor="gray.200"
                          maxW="200px"
                        >
                          <Flex alignItems="center">
                            <Text
                              className="small-font"
                              color="#475467"
                              whiteSpace="nowrap"
                              textOverflow="ellipsis"
                              overflow="hidden"
                              flex={1}
                              noOfLines={2}
                            >
                              {`${instance.data}`}
                            </Text>
                          </Flex>
                        </Td>
                        <Td maxW="10%">
                          <Flex alignItems="center" justifyContent="center">
                            <IconButton
                              flex={0.1}
                              variant="outline"
                              colorScheme="blackAlpha"
                              aria-label="Search database"
                              _hover={{
                                borderColor: 'black',
                              }}
                              onClick={() => {
                                dispatch(deleteSelectedInstance(index));
                              }}
                              size="xs"
                              icon={<DeleteIcon />}
                            />
                          </Flex>
                        </Td>
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Flex>
        </Box>
      </Box>
    </>
  );
};
